import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import moment from 'moment-timezone';
import { v4 as getId } from 'uuid';


import { FilterTrackingContext } from 'components/Providers/FilterTrackingProvider';
import { UserContext } from 'components/Providers/Providers';
import { useMutation, useQuery } from '@apollo/client';
import { useSignalrWasLoadBooked } from 'components/Providers/LoadSignalrProvider';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

import {
  BOOK_LOAD,
  REQUEST_LOCK,
  RELEASE_LOCK,
  BOOK_LOAD_IS_LOAD_BOARD,
  MAKE_OFFER
} from 'api/mutations';
import { GET_LOAD_REQS_FOR_BIN, GET_CURRENT_OFFER } from 'api/queries';
import {
  getSpecialEquipment,
  getBINLoadRequirements,
  isNotSameDay,
  useSetBookingModalUnmounted,
  getEmptyTimeString,
  useSetBookingModalLoading,
  fillTruckAndDriverInfo,
  canBookItNow,
  getHasTruckAndDriverInfo,
  useHasTruckDriverInfo
} from 'helpers/bookingModalHelpers';
import { useGetParamsForGA } from 'helpers/urlHelpers';

import {
  LOCK_DURATION_SECONDS,
  CONFIRM_SPECIALTIES,
  BOOK_FORM,
  LOCK_EXPIRED,
  BOOK_SAVING,
  BOOK_SUCCESS,
  BOOK_FAILURE,
  BOOK_LOADING,
  BOOK_LOCK_FAILURE,
  CANT_CONFIRM_SPECIALTIES,
  CAN_BOOK,
  TENDER_SUCCESS,
  REQUIRED_TRAILER_NUMBER_CUSTOMERS,
  ONLY_NUMBERS_TRAILER_NUMBER_CUSTOMERS
} from 'constants/bookingConstants';
import type { LocationSearchType } from 'sharedTypes/location/locationTypes';
import { OfferStatusId, OfferType } from 'sharedTypes/offers/offers';
import type { LoadType } from 'sharedTypes/loads/loadType';


import SUCCESS_ICON from '@arrive/ui-icons/svg/ic_circle_check_alt.svg';
import WARNING_ICON from '@arrive/ui-icons/svg/ic_warning.svg';
import LOCK_OPEN from '@arrive/ui-icons/svg/ic_lock_open.svg';
import FAILURE from 'assets/failed-cross.svg';
import TENDER_SUCCESS_ICON from 'assets/tender-success.svg';

import {
  BookingHeaderSection,
  BookingHeaderProps,
  CommonModalBody,
  CommonModalWrapper,
  CommonModalSectionContainer,
  CommonModalSectionIcon,
  CommonModalStatusReadback,
  CommonModalLoadSaveSpinner,
  CommonModalLocationReadback
} from 'components/CommonModalComponents/CommonModalComponents';
import BookingModalConfirmSpecialties from 'components/BookingModal/BookingModalConfirmSpecialties';
import BookingModalTimer from 'components/BookingModal/BookingModalTimer';
import BookingModalEmptyCity from 'components/BookingModal/BookingModalEmptyCity';
import BookingModalTruckAndDriverEntry from 'components/BookingModal/BookingModalTruckAndDriverEntry';
import BookingModalFooter from 'components/BookingModal/BookingModalFooter';
import BookingModalOffer from 'components//BookingModal/BookingModalOffer';
import useGetEmptyInfoForOffer from 'hooks/useGetEmptyInfoForOffer';
import { reportSuggestedLoadEvent } from 'helpers/formatters';
import { getHasSpecialtyText } from 'helpers/bookingModalTextHelpers';
import { getOfferStatusesForDetails } from 'utils/offersUtils';

interface HeaderSectionProps extends BookingHeaderProps {
  loadNumber: number;
  bookedSuccessfully: boolean;
  error: Error;
  isFailure: boolean;
}
const HeaderSection = ({ loadNumber, bookedSuccessfully, error, isFailure, isMAO, ...rest }: HeaderSectionProps) => {
  let headerText;

  if (!bookedSuccessfully && !error && !isFailure && isMAO) {
    headerText = `Make An Offer Arrive Load #${loadNumber}`
  } else {
    headerText = (bookedSuccessfully || !!error || isFailure) ? `Arrive Load #${loadNumber}` : `Booking Arrive Load #${loadNumber}`;
  }

  return <BookingHeaderSection headerText={headerText} isMAO={isMAO} {...rest} />;
};

const LoadSaveSpinner = ({ isSaving, isMAO }: { isSaving: boolean; isMAO: boolean }) => {
  const processingText = `Our carrier team is processing your ${isMAO ? 'offer' : 'booking'}...`;
  const savingText = isSaving ? processingText : '';
  return <CommonModalLoadSaveSpinner savingText={savingText} />;
}

interface BinAndOfferStatusReadbackProps {
  loadNumber: number;
  loadBookedError: boolean;
  binStatus: string;
  wasLoadBooked: boolean;
  isMAO: boolean;
  offerAmount?: string;
  isLoadBoard: boolean;
  ediRespondByDate?: string;
}

const ICON_MAP: { [key: string]: { id: string; viewBox: string } } = {
  warning: WARNING_ICON,
  success: SUCCESS_ICON,
  failure: FAILURE,
  lockOpen: LOCK_OPEN,
  tenderSuccess: TENDER_SUCCESS_ICON
};

const BinAndOfferStatusReadback = ({ loadNumber, loadBookedError, binStatus, wasLoadBooked, isMAO, offerAmount, isLoadBoard, ediRespondByDate }: BinAndOfferStatusReadbackProps) => {
  if (window.pendo && loadBookedError) {
    const pendoInfo = {
      loadNumber: loadNumber
    };

    window.pendo.track('BIN > Error Stolen', pendoInfo);
  }

  const [icon, setIcon] = useState<string>('warning');

  const [headerMessage, setHeaderMessage] = useState("");

  const [actionMessage, setActionMessage] = useState<React.ReactNode>();


  useEffect(() => {
    const loadBooked = wasLoadBooked && binStatus === LOCK_EXPIRED;
    if(binStatus === TENDER_SUCCESS) {
      const userTZ = moment.tz.guess()
      const respondByFormatted = moment.utc(ediRespondByDate).tz(userTZ).format('ddd, MMM D, HH:mm z')
      setHeaderMessage('Load successfully tendered.')
      setActionMessage(<ul>
        <li><span>The tender is pending your acceptance. Go accept the tender before exclusivity expires. You may accept or decline the tender by {respondByFormatted}.</span></li>
        <br></br>
        <li><span>Once accepted, check your email inbox for the rate confirmation. You may also download the rate confirmation from load details.</span></li>
      </ul>)
      setIcon('tenderSuccess')
    }
    if (binStatus === BOOK_SUCCESS) {
      if (isMAO) {
        setHeaderMessage(`Your offer of $${offerAmount} was submitted!`)
        setActionMessage("Our team will work as quickly as possible to respond to your request. " +
          "Be on the lookout for an email from us or go to 'My Offers' to see all your offer activity.");
      } else {
        const defaultCallToAction = <>Click <strong> Done</strong> to get started!</>;
        const loadBoardCallToAction = <>Click <strong>View Load</strong> to get started!</>;
        const formattedActionText = (
          <>
            Check your email inbox for the rate confirmation, or visit the <strong>Documents</strong> tab for immediate
             access to download. You can also <strong>Add/Edit Driver Information</strong> for this load right from this
             screen. {isLoadBoard ? loadBoardCallToAction : defaultCallToAction}
          </>
        );

        setHeaderMessage("Load successfully booked.")
        setActionMessage(formattedActionText)
      }

      setIcon('success');
    } else if (binStatus === BOOK_FAILURE) {
      setHeaderMessage("Sorry, we had some problems with your last request.")
      setActionMessage(`Click "Retry" and if problem persists, contact your rep.`)
      setIcon('failure')

    } else if (binStatus === BOOK_LOCK_FAILURE || loadBooked) {
      setHeaderMessage("Sorry, this load has been booked.")
      setActionMessage("Search the load board to find more available loads.")
      setIcon('warning')

    } else if (binStatus === LOCK_EXPIRED) {
      setHeaderMessage("Your time to book this load has expired.")
      setActionMessage("Try again to restart the booking process.")
      setIcon('lockOpen')
    }

  }, [binStatus, wasLoadBooked, isMAO, offerAmount, isLoadBoard, ediRespondByDate]);

  return (
    <CommonModalStatusReadback
      headerMessage={headerMessage}
      binStatus={binStatus}
      icon={ICON_MAP[icon]}
      actionMessage={actionMessage}
    />
  )
};


const isOfferAmountValid = (offerAmount: string, topSpend: number) => {
  return Number(offerAmount) > topSpend;
};

const acceptedOfferLockDuration = (offer: OfferType) => {
  const lockStartTime = moment.utc();
  const expirationTime = moment.tz(offer.expiration, 'utc');
  const durationInSeconds = expirationTime.diff(lockStartTime, 'seconds');
  return durationInSeconds < LOCK_DURATION_SECONDS ? LOCK_DURATION_SECONDS : durationInSeconds;
}

export interface Props {
  load?: LoadType;
  offer?: OfferType;
  setShowModal: (showModal: boolean) => void;
  pickupCityFilter: LocationSearchType;
  pickupDateFilter?: moment.Moment;
  onBookSuccess?: () => void;
  isLoadBoard?: boolean;
  isMAO?: boolean;
  offerAmount?: string;
  miles?: number;
  topSpend?: number;
  onOfferModalSuccess?: (newOfferAmount: string) => void;
  isAcceptAndBook?: boolean;
  isBookExpired?: boolean;
  loadNumberToFetch?: number;
  isMatch?: boolean;
  matchId?: number;
}

export default function BookingModal({
  load,
  offer,
  setShowModal,
  pickupCityFilter,
  pickupDateFilter,
  onBookSuccess,
  isLoadBoard,
  isMAO,
  offerAmount: originalOfferAmount,
  miles,
  topSpend,
  onOfferModalSuccess,
  isAcceptAndBook = false,
  isBookExpired = false,
  loadNumberToFetch,
  isMatch = false,
  matchId
}: Props) {
  const ldClient = useLDClient();
  const { FEATURE_LOAD_LOCK_CAMPING, FEATURE_LOAD_SPECIAL_SERVICES, FEATURE_NEW_OFFER_STATUS } = useFlags();
  const LoadNumber = loadNumberToFetch || (load ? load.LoadBoardId : offer.loadNumber);
  const isBookOffer = !load && !!offer;
  const fetchLoadFromLoadNumber = !load && !!loadNumberToFetch;
  const lockDuration = (!isBookOffer || isAcceptAndBook || isBookExpired) ? LOCK_DURATION_SECONDS : acceptedOfferLockDuration(offer);
  const [emptyCity, setEmptyCity] = useState(pickupCityFilter);
  const [emptyDate, setEmptyDate] = useState(pickupDateFilter ? moment(pickupDateFilter) : null);
  const [emptyTime, setEmptyTime] = useState("")
  const [lockInfo, setLockInfo] = useState(null);
  const [confirmSpecialtiesState, setConfirmSpecialtiesState] = useState("")
  const wasLoadBooked = useSignalrWasLoadBooked(LoadNumber);
  const currentUser = useContext(UserContext);
  const { pendoPayload } = useContext(FilterTrackingContext);
  const isMounted = useRef(true);
  const [isSameDay, setIsSameDay] = useState(null)
  const [newDriverInfo, setNewDriverInfo] = useState(fillTruckAndDriverInfo(load));
  const [hasTruckAndDriverInfo, setHasTruckAndDriverInfo] = useState(getHasTruckAndDriverInfo(newDriverInfo));
  const [offerAmount, setOfferAmount] = useState(originalOfferAmount);
  const [ediRespondBy, setEdiRespondBy] = useState("");
  useSetBookingModalUnmounted(isMounted);

  const { offerInfo } = useGetEmptyInfoForOffer(LoadNumber, offer?.id, !isBookOffer);

  useEffect(() => {
    if (isBookOffer && (offerInfo?.emptyDateTime && offerInfo?.emptyCity && offerInfo?.emptyCityLocation)) {

      const emptyTimeFromOffer = moment(offerInfo.emptyDateTime).format('HH:mm')
      const emptyDateFromOffer = moment(offerInfo.emptyDateTime)
      const emptyCityFromOffer = {
        ...offerInfo.emptyCityLocation,
        label: `${offerInfo.emptyCityLocation.CityName}, ${offerInfo.emptyCityLocation.CityState}`
      }

      setEmptyTime(emptyTimeFromOffer)
      setEmptyDate(emptyDateFromOffer)
      setEmptyCity(emptyCityFromOffer)
    }
  }, [isBookOffer, offerInfo])

  const { data: requestedLoad = { getLoadById: {} }, loading: loadingLoadDetails, refetch: refetchLoadDetails } = useQuery(GET_LOAD_REQS_FOR_BIN, {
    variables: {
      loadNumber: fetchLoadFromLoadNumber ? loadNumberToFetch : LoadNumber
    }
  });

  const loadDetails = (!isMatch && load) || requestedLoad.getLoadById;

  const [specialEquipment, setSpecialEquipment] = useState(() => {
    if (loadDetails) {
      return getSpecialEquipment(loadDetails)
    }
    return false;
  });
  const [loadRequirements, setLoadRequirements] = useState(() => {
    if (loadDetails) {
      return getBINLoadRequirements(loadDetails)
    }
    return false;
  });

  const hasNewSpecialties = FEATURE_LOAD_SPECIAL_SERVICES && getHasSpecialtyText(requestedLoad?.getLoadById?.LoadEquipmentSpecialServices, requestedLoad?.getLoadById?.LoadStopSpecialServices, requestedLoad?.getLoadById?.HasHazmatCommodities)
  const hasSpecialties = !isMAO && ((specialEquipment || loadRequirements) || hasNewSpecialties);

  const [binStatus, setBinStatus] = useState(() => {
    if (isBookOffer) {
      return BOOK_LOADING
    }
    return hasSpecialties ? CONFIRM_SPECIALTIES : BOOK_FORM
  });


  useEffect(() => {
    if (!loadingLoadDetails && loadDetails && !isBookOffer) {
      setIsSameDay(!isNotSameDay(loadDetails))
    }
  }, [loadingLoadDetails, isBookOffer, loadDetails])

  useEffect(() => {
    if (!loadingLoadDetails && requestedLoad.getLoadById && isBookOffer) {
      setSpecialEquipment(getSpecialEquipment(requestedLoad.getLoadById))
      setLoadRequirements(getBINLoadRequirements(requestedLoad.getLoadById))
      setNewDriverInfo(fillTruckAndDriverInfo(requestedLoad.getLoadById))
      setIsSameDay(!isNotSameDay(requestedLoad.getLoadById))
    }
  }, [requestedLoad, loadingLoadDetails, isBookOffer])

  useEffect(() => {
    if (!loadingLoadDetails && requestedLoad.getLoadById && isBookOffer && binStatus === BOOK_LOADING) {
      const hasSpecialEq = getSpecialEquipment(requestedLoad.getLoadById)
      const hasLoadReqs = getBINLoadRequirements(requestedLoad.getLoadById)
      setBinStatus(!isMAO && (hasLoadReqs || hasSpecialEq) ? CONFIRM_SPECIALTIES : BOOK_FORM)
    }
  }, [binStatus, isBookOffer, loadRequirements, loadingLoadDetails, requestedLoad, specialEquipment, isMAO]); //eslint-disable-line

  useEffect(() => {
    //Hook is repsonsible for determining if a load was booked within the time clicking the BIN button
    //Initial load request handles the possibility of the load being booked before press, was load booked for after.
    if (!loadingLoadDetails && requestedLoad.getLoadById) {
      const isLoadStatusNotAvailable = requestedLoad.getLoadById?.LoadStatus && requestedLoad.getLoadById?.LoadStatus !== "Available"
      const notAvailable = isBookOffer ? offer.status !== OfferStatusId.ACCEPTED && isLoadStatusNotAvailable : isLoadStatusNotAvailable
      const processingBook = binStatus === BOOK_SAVING || binStatus === BOOK_SUCCESS || binStatus === TENDER_SUCCESS;
      if ((notAvailable || wasLoadBooked) && !processingBook) {
        setBinStatus(BOOK_LOCK_FAILURE)
      }
    }

  }, [binStatus, isBookOffer, requestedLoad, wasLoadBooked]) //eslint-disable-line

  const getBookPrice = useCallback(() => {
    if (isBookOffer) {
      return isAcceptAndBook ? offer?.bizCounterAmount : offer?.amount;
    }

    // Else, return top spend because not book offer scenario.
    return loadDetails?.TopSpend;
  }, [offer, isAcceptAndBook, isBookOffer, loadDetails]);

  const closeModal = () => {
    setShowModal(false)
    if (lockInfo?.lockId) {
      setLoadLockHist();
      releaseLock({
        variables: {
          lockId: lockInfo.lockId,
          loadNumber: LoadNumber,
          doNotReturnLockToOriginalOwner: false
        }
      })
    }
    // User closes out of Special Reqs Modal
    if (binStatus === CONFIRM_SPECIALTIES && window.pendo) {
      const pendoInfo = {
        specialEquipment: specialEquipment,
        loadRequirements: loadRequirements,
        loadNumber: LoadNumber,
        filterCorrelationId: pendoPayload.correlationId

      };

      window.pendo.track('BIN > Special Reqs > Close Modal', pendoInfo);
    }

    if (binStatus === CANT_CONFIRM_SPECIALTIES && window.pendo) {
      const pendoInfo = {
        specialEquipment: specialEquipment,
        loadRequirements: loadRequirements,
        loadNumber: LoadNumber,
        filterCorrelationId: pendoPayload.correlationId
      };

      window.pendo.track('BIN > Does Not Meet Special Reqs', pendoInfo);
    }

    if (binStatus === BOOK_SUCCESS && onBookSuccess) {
      onBookSuccess();
    }
  }

  const handleLoadLockCamping = () => {
    const loadLockHistoryData = JSON.parse(localStorage.getItem("load-lock-history"));
    let correlationId;

    if(!loadLockHistoryData?.correlationId || !loadLockHistoryData?.time || loadLockHistoryData?.loadNumber !== LoadNumber){
      correlationId = getId();
      localStorage.setItem("load-lock-history", JSON.stringify({ correlationId }))
      return;
    }
    const currTime = new Date().toISOString();
    const diffInSeconds = moment(currTime).diff(loadLockHistoryData.time, 'seconds', true)
    const relockedWithinTime = diffInSeconds < Number(process.env.LOAD_CAMPING_INTERVAL)

    if(loadLockHistoryData.loadNumber !== LoadNumber || !relockedWithinTime){
      correlationId = getId();
      localStorage.setItem("load-lock-history", JSON.stringify({ ...loadLockHistoryData, correlationId }))
    }else{
      correlationId = loadLockHistoryData.correlationId
    }

    if(loadLockHistoryData.loadNumber === LoadNumber && relockedWithinTime){
      const pendoData = {
        CorrelationId: correlationId,
        PrevUnlockTime: new Date(loadLockHistoryData.time).toISOString(),
        RelockTime: currTime,
        LoadNumber: LoadNumber,
        OfferId: offer?.id,
        CarrierId: currentUser.CarrierId,
        CarrierCode: currentUser.CarrierCode,
        CarrierName: currentUser.CarrierName,
        CarrierRepId: currentUser?.CarrierRep?.UserId,
        CarrierRepName: currentUser?.CarrierRep?.FullName,
        UserId: currentUser?.UserId,
        UserName: currentUser.FullName
      }
      window.pendo.track('Load Relock Camping', pendoData)
    }
  }

  const [requestLock] = useMutation(REQUEST_LOCK, {
    onCompleted: (res: any) => {
      if (isMounted.current) {
        if(FEATURE_LOAD_LOCK_CAMPING){
          handleLoadLockCamping();
        }
        setLockInfo(res.requestLock);
        if (binStatus === LOCK_EXPIRED) {

          const canBook = canBookItNow(isSameDay, emptyCity, emptyDate, emptyTime);
          const offerAmountValid = isOfferAmountValid(offerAmount, topSpend);
          const canBookMAO = canBook && offerAmountValid;
          const canBookAtAll = isMAO ? canBookMAO : canBook;
          const confirmedTrailerNumIfReq = isTrailerNumRequiredCust ? newDriverInfo.TrailerNumber : true
          const confirmedOrNoSpecialties = confirmedTrailerNumIfReq && (!hasSpecialties || (hasSpecialties && confirmSpecialtiesState === "Yes"));
          const unconfirmedSpecialties = hasSpecialties && confirmSpecialtiesState === "";

          if (canBookAtAll && confirmedOrNoSpecialties) {
            setBinStatus(CAN_BOOK)
          } else if (confirmedOrNoSpecialties) {
            setBinStatus(BOOK_FORM)
          } else if (unconfirmedSpecialties) {
            setBinStatus(CONFIRM_SPECIALTIES)
          } else {
            setBinStatus(CANT_CONFIRM_SPECIALTIES);
          }
        }
      } else {
        releaseLock({
          variables: {
            lockId: res.requestLock.lockId,
            loadNumber: LoadNumber,
            doNotReturnLockToOriginalOwner: false
          }
        });
      }
    },
    onError: (res: any) => {
      setBinStatus(BOOK_LOCK_FAILURE)
      console.error(res);
    },
    context: {
      timeout: 100000 // always allow the BE service to timeout the request
    }
  });

  const [releaseLock] = useMutation(RELEASE_LOCK, {
    onCompleted: () => {
      if (isMounted.current) {
        setLockInfo(null);
      }
    },
    onError: (res: any) => {
      console.error(res);
    },
    context: {
      timeout: 100000 // always allow the BE service to timeout the request
    }
  });

  const setLoadLockHist = () => {
    const loadLockHistoryData = JSON.parse(localStorage.getItem("load-lock-history"));

    const loadLockString = JSON.stringify({
      loadNumber: LoadNumber,
      time: Date(),
      correlationId: loadLockHistoryData?.correlationId
    })

    localStorage.setItem("load-lock-history", loadLockString)
  }

  useEffect(() => {
    if (!isMAO) {
      const handleRelease = () => {
        if(FEATURE_LOAD_LOCK_CAMPING){
          setLoadLockHist();
        }
        releaseLock({
          variables: {
            lockId: lockInfo.lockId,
            loadNumber: LoadNumber,
            doNotReturnLockToOriginalOwner: false
          }
        })
      };
      if (lockInfo && lockInfo.lockId) {
        window.addEventListener('beforeunload', handleRelease);
      }

      return () => {
        window.removeEventListener('beforeunload', handleRelease)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockInfo, LoadNumber, releaseLock, isMAO, FEATURE_LOAD_LOCK_CAMPING])

  const handleRequestLock = useCallback(() => {
    requestLock({
      variables: {
        loadNumber: LoadNumber,
        durationSeconds: lockDuration
      }
    })
  }, [LoadNumber, requestLock]) //eslint-disable-line

  const setLockExpired = useCallback(() => {
    setBinStatus(BOOK_LOADING)
    if(FEATURE_LOAD_LOCK_CAMPING){
      setLoadLockHist()
    }
    //Delay the expired modal state to for CAREX-1111. Race condition causing request of a lock to return an expired lock.
    // This 2 second buffer will allow Locks Service enough time to detect the lock was expired and delete.
    setTimeout(
      () => setBinStatus(LOCK_EXPIRED),
      2000
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FEATURE_LOAD_LOCK_CAMPING])

  useEffect(() => {
    if (!isMAO) {
      handleRequestLock();
    }
  }, [handleRequestLock, isMAO])

  const gtagURLBasedAttributes = useGetParamsForGA();
  const bookingMutation = isLoadBoard ? BOOK_LOAD_IS_LOAD_BOARD : BOOK_LOAD;
  const [bookItNow, { loading, error }] = useMutation(bookingMutation, {
    onCompleted: (res: any) => {
      const succeeded = res.bookLoad?.Success;
      const wasEdiTender = res.bookLoad?.WasEdiTender;
      if (succeeded) {
        if(wasEdiTender) {
          setBinStatus(TENDER_SUCCESS)
          setEdiRespondBy(res.bookLoad?.EdiRespondBy)
          if(onBookSuccess){
            onBookSuccess();
          }
        } else {
          setBinStatus(BOOK_SUCCESS);
        }


        releaseLock({
          variables: {
            lockId: lockInfo.lockId,
            loadNumber: LoadNumber,
            doNotReturnLockToOriginalOwner: true
          }
        })

        const bookAmount = getBookPrice();

        const ldClientParams = {
          bookPrice: bookAmount,
          loadNumber: LoadNumber,
          isOffer: isBookOffer,
          offerId: offer?.id,
          screen: window.location.href,
          hasSpecialty: !!hasSpecialties
        }

        if (!isBookOffer) {
          ldClient.track(isSameDay ? 'BIN_BOOKED_SAME_DAY' : 'BIN_BOOKED_NON_SAME_DAY', ldClientParams, 1)
          if (window.pendo) {

            if(matchId){
              reportSuggestedLoadEvent('Matches > Standalone > BIN', matchId, loadDetails, offer, bookAmount, currentUser);
            }
            const pendoInfo = {
              bookPrice: bookAmount,
              loadNumber: LoadNumber,
              hasSpecialty: !!hasSpecialties,
              filterCorrelationId: pendoPayload.correlationId
            };

            window.pendo.track(!wasEdiTender ?'BIN > Book Success' : 'BIN > EDI Tender Success', pendoInfo);
          }

          if (window.gtag) {
            window.gtag('event', 'bin_book_success', {
              'carrier_code': currentUser.CarrierCode,
              'user_email': currentUser.EmailAddress,
              'first_name': currentUser.FirstName,
              'last_name': currentUser.LastName,
              'load_id': loadDetails.LoadBoardId,
              'value': loadDetails.TopSpend,
              ...gtagURLBasedAttributes
            });
          }

        } else if (isBookOffer) {
          ldClient.track(isSameDay ? 'MAO_BOOKED_SAME_DAY' : 'MAO_BOOKED_NON_SAME_DAY', ldClientParams, 1)

          if (window.pendo) {
            if(matchId){
              reportSuggestedLoadEvent('Matches > Standalone > Offer Booked', matchId, loadDetails, offer, bookAmount, currentUser, bookAmount);
            }
            window.pendo.track(!wasEdiTender ? 'Offers > Offer Booked' : 'Offers > Offer EDI Tender Success', {
              LoadNumber: loadDetails.LoadBoardId,
              BINPrice: loadDetails.TopSpend,
              OfferId: offer.id,
              OfferAmount: bookAmount,
              CarrierCode: currentUser.CarrierCode,
              CarrierName: currentUser.CarrierName,
              AccountType: 'External',
              OfferScreen: window.location.href,
              PurchaseSetting: loadDetails.IsBookItNow ? 'BIN_AND_MAO' : 'MAO_ONLY',
              hasSpecialty: !!hasSpecialties,
              filterCorrelationId: pendoPayload.correlationId
            });

            if (isAcceptAndBook) {
              window.pendo.track('Offers > EDGECounter Success', {
                LoadNumber: loadDetails.LoadBoardId,
                OfferId: offer.id,
                OfferAmount: bookAmount,
                CarrierCode: currentUser.CarrierCode,
                CarrierName: currentUser.CarrierName,
                filterCorrelationId: pendoPayload.correlationId
              })
            }
          }

          if (window.gtag) {
            window.gtag('event', 'offer_book_success', {
              'carrier_code': currentUser.CarrierCode,
              'user_email': currentUser.EmailAddress,
              'first_name': currentUser.FirstName,
              'last_name': currentUser.LastName,
              'load_id': loadDetails.LoadBoardId,
              'value': bookAmount,
              'purchase_setting': loadDetails.IsBookItNow ? 'BIN_AND_MAO' : 'MAO_ONLY',
              ...gtagURLBasedAttributes
            });
          }
        }
      }
    },
    onError: (res: any) => {
      setBinStatus(BOOK_FAILURE)
      console.error(res);
      if (window.pendo) {
        window.pendo.track('BIN > Error', {
          LoadNumber: LoadNumber,
          ErrorReason: error.message
        });
      }
    },
    context: {
      timeout: 100000 // always allow the BE service to timeout the request
    }
  });

  const [makeOffer, { loading: makeOfferLoading }] = useMutation(MAKE_OFFER, {
    onError: (err) => {
      console.error(err); //eslint-disable-line
      setBinStatus(BOOK_FAILURE);
    },
    onCompleted: (data) => {
      ldClient.track(isSameDay ? 'OFFER_CREATED_SAME_DAY' : 'OFFER_CREATED_NON_SAME_DAY', {
        offerAmount: data.makeOffer.Amount,
        BINPrice: topSpend,
        loadNumber: LoadNumber,
        offerId: data.makeOffer.id,
        screen: window.location.href
      }, 1)

      if (window.pendo) {
        if(matchId){
          reportSuggestedLoadEvent('Matches > Standalone > Offer', matchId, loadDetails, data.makeOffer, loadDetails.TopSpend, currentUser, data.makeOffer.Amount);
        }
        window.pendo.track('Offers > Make Offer Success', {
          LoadNumber: LoadNumber,
          BINPrice: topSpend,
          OfferAmount: data.makeOffer.Amount,
          CarrierCode: data.makeOffer.Carrier,
          CarrierName: data.makeOffer.CarrierName,
          OfferId: data.makeOffer.id,
          AccountType: 'External',
          OfferScreen: window.location.href,
          filterCorrelationId: pendoPayload.correlationId
        })
      }

      if (window.gtag) {
        window.gtag('event', 'make_offer', {
          'carrier_code': data.makeOffer.Carrier,
          'user_email': currentUser.EmailAddress,
          'first_name': currentUser.FirstName,
          'last_name': currentUser.LastName,
          'load_id': LoadNumber,
          ...gtagURLBasedAttributes
        });
      }
      setBinStatus(BOOK_SUCCESS);
      onOfferModalSuccess(data.makeOffer.Amount);
    },
    context: {
      timeout: 1000000
    },
    update(cache, { data }) {
      cache.writeQuery({
        query: GET_CURRENT_OFFER,
        variables: {
          loadNumber: LoadNumber,
          offerStatuses: getOfferStatusesForDetails(FEATURE_NEW_OFFER_STATUS)
        },
        data: {
          getUserOffers: {
            data: [
              {
                ...data.makeOffer,
                __typename: 'Offer'
              }
            ],
            __typename: 'OffersPayload'
          }
        }
      })
    }
  })

  useSetBookingModalLoading(loading || makeOfferLoading, setBinStatus);
  useHasTruckDriverInfo(newDriverInfo, setHasTruckAndDriverInfo);

  useEffect(() => {
    if (binStatus === BOOK_FORM && window.pendo) {
      const pendoInfo = {
        bookPrice: getBookPrice(),
        loadNumber: LoadNumber,
        filterCorrelationId: pendoPayload.correlationId
      };

      window.pendo.track('BIN > BIN Form Open', pendoInfo);
    }
  }, [binStatus]); // eslint-disable-line

  const hasValidTrailerNumber = (trailerNumber: string, truckNumber: string) => {
    if(!trailerNumber
      || trailerNumber.length > 50
      || trailerNumber === truckNumber
    ){
      return false
    }
    return isTrailerNumberOnlyNumbers ? /^\d*$/.test(trailerNumber) : /^[a-zA-Z0-9]*$/.test(trailerNumber);
  }

  useEffect(() => {
    const ableToBin = canBookItNow(isSameDay, emptyCity, emptyDate, emptyTime);
    const validOfferAmount = isOfferAmountValid(offerAmount, topSpend);
    const hasTrailerNumIfRequired = isTrailerNumRequiredCust ? hasValidTrailerNumber(newDriverInfo.TrailerNumber, newDriverInfo.TruckNumber) : true
    if (binStatus === CAN_BOOK && (!ableToBin || !hasTrailerNumIfRequired || (isMAO && !validOfferAmount))) {
      setBinStatus(BOOK_FORM)
    } else if (binStatus === BOOK_FORM && ableToBin && hasTrailerNumIfRequired && (isMAO ? validOfferAmount : true)) {
      setBinStatus(CAN_BOOK)
    }
  }, [emptyCity, emptyDate, emptyTime, binStatus, isSameDay, offerAmount, newDriverInfo.TrailerNumber, newDriverInfo.TruckNumber]); // eslint-disable-line

  const handleBookItNow = () => {
    const bookingAmount = getBookPrice();

    if (window.pendo) {
      const pendoInfo = {
        bookPrice: bookingAmount,
        loadNumber: LoadNumber,
        filterCorrelationId: pendoPayload.correlationId
      };

      if (error && error.message) {
        window.pendo.track('BIN > Retry', pendoInfo);

      } else {
        window.pendo.track('BIN > Final BIN Click', pendoInfo);
      }
    }

    if (canBookItNow(isSameDay, emptyCity, emptyDate, emptyTime) && isMAO ? isOfferAmountValid(offerAmount, topSpend) : true) {
      const emptyTimeString = getEmptyTimeString(isSameDay, loadDetails, emptyDate, emptyTime, emptyCity);

      const bookingParams = {
        LoadNumber: LoadNumber,
        TopSpend: bookingAmount,
        OfferAmount: offer ? bookingAmount : undefined,
        RowVersion: loadDetails.RowVersion,
        LocationIANACode: loadDetails.PickupLocationIANACode,
        EmptyCity: emptyCity.CityName,
        EmptyCityId: emptyCity.Value,
        EmptyGeoCode: emptyCity.Geolocation,
        EmptyStateCode: emptyCity.CityState,
        EmptyDateTime: emptyTimeString,
        Miles: loadDetails.Miles,
        CustomerRepEmailAddress: loadDetails.CustomerRepEmailAddress,
        OfferId: offer ? offer.id : undefined,
        DriverInfo: newDriverInfo
      };

      if (offer && moment().isSameOrAfter(moment.tz(offer.expiration, 'utc'), 'minute')) {
        refetchLoadDetails().then(({ data: { getLoadById } }: { data: { getLoadById: LoadType } }) => {
          bookItNow({
            variables: {
              bookLoad: {
                ...bookingParams,
                RowVersion: getLoadById.RowVersion
              }
            }
          })
        });
      } else {
        bookItNow({
          variables: {
            bookLoad: bookingParams
          }
        })
      }
    }
  };

  const handleSubmitOffer = () => {
    const emptyTimeString = moment.tz(`${emptyDate?.format('MM/DD/YYYY')} ${emptyTime || '00:00'}`, 'MM/DD/YYYY HH:mm', emptyCity?.LocationIANACode).toISOString();

    makeOffer({
      variables: {
        loadNumber: LoadNumber,
        offerAmount: Number(offerAmount),
        carrierRep: currentUser.CarrierRep ? {
          FirstName: currentUser.CarrierRep.FirstName,
          LastName: currentUser.CarrierRep.LastName,
          EmailAddress: currentUser.CarrierRep.EmailAddress
        } : undefined,
        emptyDateTime: emptyTimeString || undefined,
        emptyCity: {
          emptyCityId: emptyCity?.Id || undefined,
          emptyCity: emptyCity?.CityName || undefined,
          emptyCityStateCode: emptyCity?.CityState || undefined,
          emptyCityGeocodeText: emptyCity?.Geolocation || undefined
        }
      }
    });
  };

  const handleSpecialRecsContact = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    if (window.pendo && binStatus === CONFIRM_SPECIALTIES) {
      const pendoInfo = {
        specialEquipment: specialEquipment,
        loadRequirements: loadRequirements,
        loadNumber: LoadNumber,
        filterCorrelationId: pendoPayload.correlationId
      };

      window.pendo.track('BIN > Special Reqs > Contact Rep', pendoInfo);
    }
  };
  const showInfoStatus = binStatus === BOOK_SUCCESS || binStatus === BOOK_FAILURE || binStatus === BOOK_LOCK_FAILURE || binStatus === LOCK_EXPIRED || binStatus === TENDER_SUCCESS;
  const loadBookedError = error ? error.message.includes('Carrier can not be updated on unfinished loads') : false;
  const showLockTimer = binStatus !== BOOK_SUCCESS && binStatus !== BOOK_FAILURE && binStatus !== BOOK_LOCK_FAILURE && binStatus !== LOCK_EXPIRED && binStatus !== TENDER_SUCCESS;
  const isLoadingOrSaving = binStatus === BOOK_LOADING || binStatus === BOOK_SAVING || loadingLoadDetails;
  const showBookForm = binStatus !== BOOK_SUCCESS && binStatus !== BOOK_FAILURE && binStatus !== BOOK_LOCK_FAILURE && binStatus !== LOCK_EXPIRED && binStatus !== TENDER_SUCCESS;
  const emptyCitySectionValid = isMAO ? canBookItNow(isSameDay, emptyCity, emptyDate, emptyTime) : binStatus === CAN_BOOK;
  const isFailure = binStatus === BOOK_FAILURE || binStatus === BOOK_LOCK_FAILURE;
  const customerCode = (load?.CustomerCode ?? loadDetails?.CustomerCode)?.toUpperCase();
  const isTrailerNumRequiredCust = REQUIRED_TRAILER_NUMBER_CUSTOMERS.includes(customerCode);
  const isTrailerNumberOnlyNumbers = ONLY_NUMBERS_TRAILER_NUMBER_CUSTOMERS.includes(customerCode);

  const specialServicesData = {
    LoadEquipmentSpecialServices: requestedLoad.getLoadById?.LoadEquipmentSpecialServices,
    LoadStopSpecialServices: requestedLoad.getLoadById?.LoadStopSpecialServices,
    HasHazmatCommodities: requestedLoad.getLoadById?.HasHazmatCommodities
  }

  return (
    <CommonModalWrapper setShowModal={closeModal} loading={isLoadingOrSaving}>
      {isLoadingOrSaving && (
        <LoadSaveSpinner isSaving={binStatus === BOOK_SAVING} isMAO={isMAO} />
      )}
      {!isLoadingOrSaving &&
        <>
          {lockInfo && showLockTimer && <BookingModalTimer lockStartTime={lockInfo.lockDateTime} setLockExpired={setLockExpired} lockDuration={lockDuration} />}
          <HeaderSection
            loadNumber={LoadNumber}
            loadDetails={loadDetails}
            offer={offer}
            bookedSuccessfully={binStatus === BOOK_SUCCESS}
            isFailure={isFailure}
            error={error}
            closeModal={closeModal}
            isMAO={isMAO}
            isAcceptAndBook={isAcceptAndBook}
          />
          <CommonModalLocationReadback load={loadDetails} />
          <CommonModalBody>
            {showBookForm &&
              <>
                {(hasSpecialties && !isMAO) &&
                  <CommonModalSectionContainer>
                    <CommonModalSectionIcon
                      number={1}
                      isVisible
                      isValid={binStatus === CONFIRM_SPECIALTIES ? null : (binStatus !== CANT_CONFIRM_SPECIALTIES)}
                    />
                    <BookingModalConfirmSpecialties
                      loadNumber={LoadNumber}
                      specialEquipment={specialEquipment}
                      loadRequirements={loadRequirements}
                      setBinStatus={setBinStatus}
                      binStatus={binStatus}
                      confirmSpecialtiesState={confirmSpecialtiesState}
                      setConfirmSpecialtiesState={setConfirmSpecialtiesState}
                      specialServicesData={specialServicesData}
                    />
                  </CommonModalSectionContainer>
                }
                {isMAO &&
                  <CommonModalSectionContainer>
                    <CommonModalSectionIcon
                      number={1}
                      isVisible
                      isValid={!isOfferAmountValid(offerAmount, topSpend) ? null : true}
                    />
                    <BookingModalOffer
                      offerAmount={offerAmount}
                      setOfferAmount={setOfferAmount}
                      miles={miles}
                      topSpend={topSpend}
                    />
                  </CommonModalSectionContainer>
                }
                {binStatus !== CANT_CONFIRM_SPECIALTIES &&
                  <CommonModalSectionContainer>
                    <CommonModalSectionIcon
                      number={hasSpecialties || isMAO ? 2 : 1}
                      isVisible={binStatus === BOOK_FORM || binStatus === CAN_BOOK}
                      isValid={!emptyCitySectionValid ? null : true}
                    />
                    <BookingModalEmptyCity
                      emptyCity={emptyCity}
                      setEmptyCity={setEmptyCity}
                      emptyDate={emptyDate}
                      setEmptyDate={setEmptyDate}
                      emptyTime={emptyTime}
                      setEmptyTime={setEmptyTime}
                      sectionIsActive={binStatus === BOOK_FORM || binStatus === CAN_BOOK}
                      showDateAndTime={isSameDay}
                      pickupLocationIANACode={loadDetails.PickupLocationIANACode}
                    />
                  </CommonModalSectionContainer>
                }
                {(!isMAO && (isSameDay || isTrailerNumRequiredCust) && binStatus !== CANT_CONFIRM_SPECIALTIES) &&
                  <CommonModalSectionContainer>
                    <CommonModalSectionIcon
                      number={hasSpecialties ? 3 : 2}
                      isVisible={binStatus === CAN_BOOK}
                      isValid={(hasTruckAndDriverInfo && binStatus === CAN_BOOK) ? true : null}
                    />
                    <BookingModalTruckAndDriverEntry
                      isSameDay={isSameDay}
                      isTrailerNumRequiredCust={isTrailerNumRequiredCust}
                      sectionIsActive={(isSameDay && !isTrailerNumRequiredCust) ? binStatus === CAN_BOOK : (binStatus === BOOK_FORM || binStatus === CAN_BOOK )}
                      setNewDriverInfo={setNewDriverInfo}
                      newDriverInfo={newDriverInfo}
                      isTeamLoad={loadDetails.IsTeam}
                    />
                  </CommonModalSectionContainer>
                }
              </>
            }
            {showInfoStatus &&
              <BinAndOfferStatusReadback
                loadNumber={LoadNumber}
                loadBookedError={loadBookedError}
                binStatus={binStatus}
                wasLoadBooked={wasLoadBooked}
                isMAO={isMAO}
                offerAmount={offerAmount}
                isLoadBoard={isLoadBoard}
                ediRespondByDate={ediRespondBy}
              />
            }
            <BookingModalFooter
              closeModal={closeModal}
              isLoadBoard={isLoadBoard}
              handleBookItNow={handleBookItNow}
              handleRequestLock={handleRequestLock}
              binStatus={binStatus}
              loadNumber={LoadNumber}
              load={loadDetails}
              handleSpecialRecsContact={handleSpecialRecsContact}
              isMAO={isMAO}
              handleSubmitOffer={handleSubmitOffer}
              wasLoadBooked={wasLoadBooked}
            />
          </CommonModalBody>
        </>
      }
    </CommonModalWrapper>
  )
}
